/* eslint-disable no-await-in-loop */
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { showErrorMessage } from 'utils/showErrorMessage';

export type GetTokenSilently = () => Promise<string | null>;
type LogoutFunction = () => Promise<void>;

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _logoutAttempted?: boolean;
}

const withAuthorizationHeader = (
  config: InternalAxiosRequestConfig,
  token: string | null
): InternalAxiosRequestConfig => {
  if (!token) {
    return config;
  }

  const headers = AxiosHeaders.from(config.headers ?? {});
  headers.set('Authorization', `Bearer ${token}`);

  return {
    ...config,
    headers,
  };
};

const axiosV1 = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

const axiosV2 = axios.create({
  baseURL: process.env.REACT_APP_API_URL_V2,
});

let tokenPromise: Promise<string> | null = null;
let isLoggingOut = false;

const redirectToLogin = () => {
  window.location.href = `#/login?returnUrl=${encodeURIComponent(
    window.location.pathname
  )}`;
};

export const setupInterceptors = (
  getTokenSilently: () => Promise<GetTokenSilently | null>,
  logout: LogoutFunction
) => {
  const addAuthInterceptor = (axiosInstance: AxiosInstance) => {
    axiosInstance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        try {
          if (!tokenPromise) {
            tokenPromise = getTokenSilently() as unknown as Promise<string>;

            setTimeout(() => {
              tokenPromise = null;
            }, 50000);
          }

          const token = await tokenPromise;
          return withAuthorizationHeader(config, token);
        } catch (error) {
          console.error('Error getting access token:', error); // eslint-disable-line no-console
          tokenPromise = null;

          redirectToLogin();

          return Promise.reject(error);
        }
      },
      (error: AxiosError) => {
        redirectToLogin();

        return Promise.reject(error);
      }
    );

    axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,

      async (error: AxiosError) => {
        if (!error.config) {
          redirectToLogin();
          return Promise.reject(error);
        }
        const originalConfig = error.config as RetryConfig;
        const config = originalConfig as RetryConfig;
        const errorData = error.response?.data as
          | { code?: string }
          | undefined;

        if (
          (!originalConfig._logoutAttempted &&
            errorData?.code === 'SESSION_EXPIRED') ||
          errorData?.code === 'SESSION_INVALID' ||
          errorData?.code === 'TOKEN_EXPIRED'
        ) {
          originalConfig._logoutAttempted = true;

          if (!isLoggingOut) {
            isLoggingOut = true;
            try {
              await logout();
              redirectToLogin();
            } catch (logoutError) {
              redirectToLogin();
            } finally {
              isLoggingOut = false;
            }
          } else {
            while (isLoggingOut) {
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
            redirectToLogin();
          }

          throw new Error('Session expired. Please log in again.');
        }

        if (error.response?.status === 401 && !config._retry) {
          config._retry = true;

          try {
            tokenPromise = null;
            const token = await getTokenSilently();
            const retryConfig = withAuthorizationHeader(
              config,
              token as string | null
            );

            return axiosInstance(retryConfig);
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError); // eslint-disable-line no-console
            redirectToLogin();
            return Promise.reject(refreshError);
          }
        }

        if (error.response?.status === 401 || error.response?.status === 403) {
          showErrorMessage(error);
          redirectToLogin();
        }

        return Promise.reject(error);
      }
    );
  };

  addAuthInterceptor(axiosV1);
  addAuthInterceptor(axiosV2);
};

export { axiosV1, axiosV2 };
